import { AspectRatio, Box, Flex, GridItem, Text, hasCloudinaryAsset, CldImage } from "@project/ui";
import { motion } from "framer-motion";
import type { ModuleImageFieldsFragment } from "../module-image/__generated/module-image.contentful.generated";
import type { GalleryLayoutConfig } from "@/components/contentful/module-gallery/galleryLayoutConfig";

const GalleryItem = ({
    config,
    galleryItem,
    onItemClick,
}: {
    config: GalleryLayoutConfig;
    galleryItem: ModuleImageFieldsFragment;
    onItemClick: () => void;
}) => {
    const { grid, gridItem, gridItemAsset } = config;

    return (
        <GridItem
            {...grid}
            key={galleryItem?.description ?? galleryItem?.asset[0].public_id}
            as={motion.div}
            variants={{
                initial: { opacity: 0, y: 20, transition: { type: "tween" } },
                animate: { opacity: 1, y: 0, transition: { type: "tween" } },
            }}
            exit={{ opacity: 0, y: 20 }}
            overflow={gridItem.fillVertical ? "hidden" : "auto"}
            rounded="large"
        >
            <AspectRatio
                ratio={gridItem.aspectRatio}
                height={gridItem.fillVertical ? "100%" : "auto"}
                bgColor="grey100"
                rounded="large"
            >
                <Box
                    rounded="large"
                    as="button"
                    onClick={onItemClick}
                    position="relative"
                    overflow="hidden"
                    display="block"
                    _hover={{
                        ":after, .galleryItemDescription": {
                            opacity: 1,
                            transitionDuration: "var(--transition-duration-long)",
                            transitionTimingFunction: "var(--transition-easing-in)",
                            transitionProperty: "opacity",
                        },
                    }}
                    _after={{
                        content: "''",
                        position: "absolute",
                        inset: 0,
                        bgColor: "backgroundShading",
                        opacity: 0,
                        borderRadius: "medium",
                    }}
                    aria-label="Open gallery item"
                >
                    {hasCloudinaryAsset(galleryItem?.asset) && (
                        <CldImage
                            cloudinaryAsset={galleryItem?.asset}
                            alt={galleryItem.alt ?? ""}
                            sizes={gridItemAsset.sizes}
                            aspectRatio={gridItemAsset.aspectRatio}
                            gravity="auto:subject"
                            crop="fill"
                        />
                    )}

                    {galleryItem?.description ? (
                        <Flex
                            className="galleryItemDescription"
                            minH="100px"
                            position="absolute"
                            right={0}
                            bottom={0}
                            left={0}
                            zIndex="1"
                            color="allWhite"
                            px={10}
                            textAlign="left"
                            alignItems="center"
                            opacity={0}
                            borderBottomRadius="medium"
                            transitionDuration="var(--transition-duration-short)"
                            transitionTimingFunction="var(--transition-easing-in)"
                            transitionProperty="opacity"
                            bg="linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 115%)"
                        >
                            <Text>{galleryItem?.description}</Text>
                        </Flex>
                    ) : null}
                </Box>
            </AspectRatio>
        </GridItem>
    );
};

export { GalleryItem };
